import React, { useState, useEffect, useRef } from "react";
import axios from "./../../utils/axios";
import { getUser } from "../../utils/auth";

const PropertyManager = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [circleFilter, setCircleFilter] = useState("");
  const [pagination, setPagination] = useState({ page: 1, pages: 1, limit: 20, total: 0 });

  const userId = getUser()?.id;
  const fileInputRef = useRef();
  const [image, setImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    pin: "",
    circle: "",
    occupationstatus: "",
    floor_admin: "",
    landuse_admin: "",
    landarea_admin: "",
    coveredarea_admin: "",
    location: "",
    floors_ml: "",
    landuse_ml: "",
    landarea_ml: "",
    coveredarea_ml: "",
    exp_garv: "",
    admin_garv: "",
    groupes: "",
    userId: "",
  });

  // Fetch properties with pagination
  const fetchProperties = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get("/properties", { params: { page } });
      const { data, pagination } = response.data;
      setProperties(data || []);
      setPagination(pagination);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties(pagination.page);
  }, []);

  // Handle circle filter
  const filteredProperties = circleFilter
    ? properties.filter((p) => p.circle === circleFilter)
    : properties;

  const uniqueCircles = [...new Set(properties.map((p) => p.circle).filter(Boolean))];

  const handleFilterChange = (e) => {
    setCircleFilter(e.target.value);
  };

  const openForm = (prop = null) => {
    setSelectedProperty(prop);
    if (prop) {
      setFormData({ ...prop });
      setImage(null);
    } else {
      setFormData({
        pin: "",
        circle: "",
        occupationstatus: "",
        floor_admin: "",
        landuse_admin: "",
        landarea_admin: "",
        coveredarea_admin: "",
        location: "",
        floors_ml: "",
        landuse_ml: "",
        landarea_ml: "",
        coveredarea_ml: "",
        exp_garv: "",
        admin_garv: "",
        groupes: "",
      });
      setImage(null);
    }
    setFormOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => setImage(e.target.files[0]);
  const openFilePicker = () => fileInputRef.current.click();

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => data.append(key, formData[key]));
      if (userId) data.append("userId", userId);
      if (image) data.append("image", image);

      let response;
      if (selectedProperty) {
        response = await axios.put(`/properties/data/${formData.pin}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await axios.post("/properties", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      alert(response.data.message);
      setFormOpen(false);
      setImage(null);
      fetchProperties(pagination.page);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Something went wrong!");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.pages) return;
    fetchProperties(newPage);
  };

  return (
    <div className="max-w-6xl mx-auto my-6">
      {/* Filter & Add */}
      <div className="mb-4 flex items-center gap-4">
        <label className="font-medium">Filter by Circle:</label>
        <select
          value={circleFilter}
          onChange={handleFilterChange}
          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All</option>
          {uniqueCircles.map((circle) => (
            <option key={circle} value={circle}>
              {circle}
            </option>
          ))}
        </select>
        <button
          onClick={() => openForm(null)}
          className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Add New Property
        </button>
      </div>

      {/* Properties Table */}
      <div className="overflow-x-auto bg-white shadow-md rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">PIN</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Circle</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Occupation</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Floor</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Land Use</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Covered Area</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Image</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Group</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="9" className="px-4 py-4 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : filteredProperties.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-4 py-4 text-center text-gray-500">
                  No properties found
                </td>
              </tr>
            ) : (
              filteredProperties.map((prop) => (
                <tr key={prop.pin}>
                  <td className="px-4 py-2">{prop.pin}</td>
                  <td className="px-4 py-2">{prop.circle}</td>
                  <td className="px-4 py-2">{prop.occupationstatus}</td>
                  <td className="px-4 py-2">{prop.floor_admin}</td>
                  <td className="px-4 py-2">{prop.landuse_admin}</td>
                  <td className="px-4 py-2">{prop.coveredarea_admin}</td>
                  <td className="px-4 py-2">
                    {prop.image ? (
                      <img src={prop.image} alt="Property" className="w-16 h-16 object-cover rounded" />
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td className="px-4 py-2">{prop.groupes}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => openForm(prop)}
                      className="px-3 py-1 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
                    >
                      Update
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center items-center mt-4 gap-2">
        <button
          onClick={() => handlePageChange(pagination.page - 1)}
          disabled={pagination.page === 1}
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          Previous
        </button>
        <span>
          Page {pagination.page} of {pagination.pages}
        </span>
        <button
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={pagination.page === pagination.pages}
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* Property Form */}
      {formOpen && (
        <div className="p-6 bg-white shadow-md rounded-md mt-6">
          <h2 className="text-2xl font-bold mb-4">
            {selectedProperty ? "Update Property" : "Add Property"}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.keys(formData).map((key) => (
              <div key={key} className="flex flex-col">
                <label className="mb-1 font-medium text-gray-700">
                  {key.replace(/_/g, " ").toUpperCase()}
                </label>
                <input
                  type="text"
                  name={key}
                  value={formData[key] || ""}
                  onChange={handleChange}
                  disabled={key === "pin" && selectedProperty}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}

            {/* Image Upload */}
            <div className="flex flex-col md:col-span-2">
              <label className="mb-1 font-medium text-gray-700">Image</label>
              <button
                type="button"
                onClick={openFilePicker}
                className="mb-2 px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                {image ? "Change Image" : "Select Image"}
              </button>
              <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
              {image && <p className="text-gray-700 mt-1">Selected: {image.name}</p>}
              {selectedProperty?.image && !image && (
                <p className="text-gray-500 mt-1">Current Image: {selectedProperty.image}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="md:col-span-2">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting
                  ? selectedProperty
                    ? "Updating..."
                    : "Submitting..."
                  : selectedProperty
                  ? "Update Property"
                  : "Add Property"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyManager;
